import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import CustomerActivityHistory from '@/components/CustomerActivityHistory';
import EstimateQA from '@/components/EstimateQA';
import VehiclesSection from '@/components/VehiclesSection';
import { resolveNiche } from '@/lib/nichePresets';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  MapPin, 
  Edit2, 
  Archive,
  FileText,
  Receipt,
  Save,
  X,
  Download,
  ClipboardList,
  Camera,
  PenTool,
  Eye,
  StickyNote,
  Image as ImageIcon,
  Expand,
  Clock,
  Calendar,
  MessageSquare,
  MessageCircle,
  ChevronDown,
  Plus,
  Briefcase,
  CheckCircle,
  XCircle,
  DollarSign,
  Trash2,
  CalendarCheck,
  AlertCircle,
  AlertTriangle,
  Ruler,
  Users
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import PDFPreviewModal from '@/components/PDFPreviewModal';
import PhotoLightbox from '@/components/PhotoLightbox';
import { Textarea } from '@/components/ui/textarea';
import { getFieldNotesDraft } from '@/lib/offlineSync';

const safeText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v.map(item => typeof item === 'string' ? item : (item?.content || item?.text || '')).filter(Boolean).join('\n');
  }
  if (typeof v === 'object') {
    // Extract common text fields
    return v.content || v.text || v.notes || JSON.stringify(v);
  }
  return String(v);
};

/**
 * Conversations tab — shows Q&A threads across all of this customer's estimates.
 * Contractor can reply directly here and the message will also show on the estimate page.
 */
const ConversationsTab = ({ clientId, client, estimates, organization, user }) => {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [estimatesWithCounts, setEstimatesWithCounts] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!organization?.id || !estimates?.length) {
        setEstimatesWithCounts([]);
        setLoading(false);
        return;
      }
      // Only load comment counts for estimates that are sent/approved/declined
      const filtered = estimates.filter(e => ['sent', 'approved', 'declined'].includes(e.status));
      const withCounts = await Promise.all(
        filtered.map(async (e) => {
          try {
            const r = await fetch(
              `${apiBase}/api/organizations/${organization.id}/estimates/${e.id}/comments`
            );
            if (!r.ok) return { ...e, _commentCount: 0, _hasUnread: false };
            const d = await r.json();
            return { ...e, _commentCount: d.count || 0, _hasUnread: !!e.has_unread_customer_comment };
          } catch {
            return { ...e, _commentCount: 0, _hasUnread: false };
          }
        })
      );
      // Show estimates with comments first, then unread, then most recent
      withCounts.sort((a, b) => {
        if (a._hasUnread !== b._hasUnread) return a._hasUnread ? -1 : 1;
        if ((b._commentCount || 0) !== (a._commentCount || 0)) return (b._commentCount || 0) - (a._commentCount || 0);
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
      setEstimatesWithCounts(withCounts);
      // Auto-expand the first one with comments
      const firstWithComments = withCounts.find(e => e._commentCount > 0);
      if (firstWithComments) setExpandedId(firstWithComments.id);
      setLoading(false);
    };
    load();
  }, [apiBase, organization?.id, estimates, clientId]);

  if (loading) {
    return <div className="card-default text-center text-muted-foreground py-8">Loading conversations…</div>;
  }
  if (!estimatesWithCounts.length) {
    return (
      <div className="card-default text-center py-10">
        <MessageCircle className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="font-semibold text-gray-700">No conversations yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Once you send {client?.name || 'this customer'} an estimate, any questions they ask will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="conversations-tab">
      <div className="card-default">
        <h2 className="font-heading text-lg sm:text-xl font-semibold mb-1">
          Conversations with {client?.name}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Reply here or from the estimate — both stay in sync. {client?.email && <>The customer gets an email at <strong>{client.email}</strong> with a link back to the estimate.</>}
        </p>
        <div className="space-y-2">
          {estimatesWithCounts.map(e => {
            const isOpen = expandedId === e.id;
            return (
              <div key={e.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`conversation-${e.id}`}>
                <button
                  onClick={() => setExpandedId(isOpen ? null : e.id)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate">
                        EST-{(e.id || '').slice(-8).toUpperCase()} · {e.service_type || 'Estimate'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {e._commentCount > 0 ? `${e._commentCount} message${e._commentCount === 1 ? '' : 's'}` : 'No messages yet'}
                        {e.status && ` · ${e.status}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {e._hasUnread && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold uppercase">New</span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-200 p-3 bg-gray-50/30">
                    <EstimateQA
                      mode="contractor"
                      orgId={organization?.id}
                      estimateId={e.id}
                      defaultAuthorName={user?.name || ''}
                      defaultAuthorEmail={user?.email || ''}
                      primaryColor={organization?.brand_primary_color || '#1e3a8a'}
                      user={user}
                      customerName={client?.name}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ClientDetailPage = () => {  const { clientId } = useParams();
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [client, setClient] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);  // Add site visits
  const [fieldNoteDrafts, setFieldNoteDrafts] = useState([]);  // Add drafts
  const [activityLog, setActivityLog] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [lightboxState, setLightboxState] = useState({ isOpen: false, photos: [], initialIndex: 0, title: '' });

  useEffect(() => {
    loadClientData();
  }, [clientId, organization?.id]);

  const loadClientData = async () => {
    if (!organization?.id || !clientId) return;
    
    try {
      // Load client first — if this fails, the page can't render
      const clientRes = await apiHelpers.getClient(organization.id, clientId);
      setClient(clientRes.data);
      setEditData(clientRes.data);
      setClientNotes(Array.isArray(clientRes.data?.notes) ? clientRes.data.notes : []);
      
      // Load related data in parallel — failures here shouldn't break the page
      const [estimatesRes, invoicesRes, jobsRes, siteVisitsRes] = await Promise.all([
        apiHelpers.getEstimates(organization.id, '', clientId).catch(() => ({ data: [] })),
        apiHelpers.getInvoices(organization.id, '', clientId).catch(() => ({ data: [] })),
        apiHelpers.getJobs(organization.id, { clientId }).catch(() => ({ data: [] })),
        apiHelpers.getSiteVisits(organization.id, { clientId }).catch(() => ({ data: [] }))
      ]);
      
      setEstimates(estimatesRes.data || []);
      setInvoices(invoicesRes.data || []);
      setJobs(jobsRes.data || []);
      setSiteVisits(siteVisitsRes.data || []);
      
      // Load auto-saved field note drafts from IndexedDB (non-blocking)
      try {
        const drafts = [];
        for (const visit of (siteVisitsRes.data || [])) {
          try {
            const draft = await getFieldNotesDraft(visit.id);
            if (draft) {
              drafts.push({
                siteVisitId: visit.id,
                siteVisit: visit,
                draft: draft,
                savedAt: draft.savedAt || new Date().toISOString()
              });
            }
          } catch (e) {
            // Ignore individual draft load errors
          }
        }
        setFieldNoteDrafts(drafts);
      } catch (e) {
        // IndexedDB failures shouldn't break the page
      }
      
      // Build activity timeline from estimates, invoices, jobs
      buildActivityTimeline(estimatesRes.data || [], invoicesRes.data || [], jobsRes.data || []);
    } catch (error) {
      console.error('Failed to load client:', error);
      toast.error('Failed to load client profile');
      navigate('/clients');
    } finally {
      setIsLoading(false);
    }
  };

  const buildActivityTimeline = (estimates, invoices, jobs) => {
    const activities = [];
    
    // Add estimates
    estimates.forEach(est => {
      activities.push({
        id: est.id,
        type: 'estimate',
        action: est.status === 'approved' ? 'Estimate approved' : 
                est.status === 'declined' ? 'Estimate declined' :
                est.status === 'sent' ? 'Estimate sent' : 'Estimate created',
        title: est.title || `Estimate #${est.id?.slice(0, 8).toUpperCase()}`,
        amount: est.total,
        date: est.sent_at || est.created_at,
        status: est.status,
        link: `/estimates/${est.id}`
      });
    });
    
    // Add invoices
    invoices.forEach(inv => {
      activities.push({
        id: inv.id,
        type: 'invoice',
        action: inv.status === 'paid' ? 'Invoice paid' : 
                inv.status === 'partial' ? 'Partial payment received' : 'Invoice sent',
        title: `Invoice #${inv.id?.slice(0, 8).toUpperCase()}`,
        amount: inv.status === 'paid' ? inv.amount_due : inv.amount_paid || 0,
        date: inv.paid_at || inv.sent_at || inv.created_at,
        status: inv.status,
        link: `/invoices/${inv.id}`
      });
    });
    
    // Add jobs
    jobs.forEach(job => {
      activities.push({
        id: job.id,
        type: 'job',
        action: job.status === 'completed' ? 'Job completed' :
                job.status === 'in_progress' ? 'Job started' : 'Job scheduled',
        title: job.title || job.address || 'Job',
        date: job.start_date || job.created_at,
        status: job.status,
        link: `/jobs?selected=${job.id}`
      });
    });
    
    // Sort by date descending
    activities.sort((a, b) => new Date(b.date) - new Date(a.date));
    setActivityLog(activities);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    const note = {
      id: `note-${Date.now()}`,
      content: newNote,
      created_at: new Date().toISOString(),
      created_by: user?.name || 'Unknown'
    };
    
    const updatedNotes = [note, ...clientNotes];
    
    try {
      await apiHelpers.updateClient(organization.id, clientId, { notes: updatedNotes }, user?.id);
      setClientNotes(updatedNotes);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    const updatedNotes = clientNotes.filter(n => n.id !== noteId);
    
    try {
      await apiHelpers.updateClient(organization.id, clientId, { notes: updatedNotes }, user?.id);
      setClientNotes(updatedNotes);
      toast.success('Note deleted');
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleSave = async () => {
    try {
      const response = await apiHelpers.updateClient(
        organization.id,
        clientId,
        editData,
        user.id
      );
      
      setClient(response.data);
      setIsEditing(false);
      toast.success('Client updated');
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error('Failed to update client');
    }
  };

  const handleArchive = async () => {
    const confirmed = await showConfirm({
      title: 'Archive Customer?',
      description: 'They will be removed from your customer list but their records will be kept for your reference.',
      variant: 'archive',
      confirmText: 'Archive',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await apiHelpers.deleteClient(organization.id, clientId, user.id);
      toast.success('Client archived successfully');
      navigate('/clients');
    } catch (error) {
      console.error('Failed to archive client:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to archive client';
      toast.error(errorMsg);
    }
  };

  // Collect all field notes from estimates, site visits, and drafts
  const getAllFieldNotes = () => {
    const notes = [];
    
    // Add field note drafts (auto-saved but not yet submitted)
    fieldNoteDrafts.forEach(draftItem => {
      notes.push({
        id: `draft-${draftItem.siteVisitId}`,
        type: 'draft',
        siteVisitId: draftItem.siteVisitId,
        serviceType: draftItem.siteVisit?.service_type || 'General',
        jobAddress: draftItem.siteVisit?.address,
        date: draftItem.savedAt,
        internalNotes: draftItem.draft?.internalNotes,
        clientNotes: draftItem.draft?.clientNotes,
        photos: draftItem.draft?.photos || [],
        measurements: draftItem.draft?.measurements,
        structuredMeasurements: draftItem.draft?.structuredMeasurements,
        checklist: draftItem.draft?.checklist,
        siteConditions: draftItem.draft?.siteConditions,
        clientExpectations: draftItem.draft?.clientExpectations,
        materialsIdeas: draftItem.draft?.materialsIdeas,
        projectNotes: draftItem.draft?.projectNotes,
        isDraft: true
      });
    });
    
    // Add site visit field notes (submitted)
    siteVisits.forEach(visit => {
      // Include if any field notes data exists
      const hasFieldData = visit.field_notes || visit.internal_notes || 
        visit.project_notes || visit.materials_scope_ideas ||
        (visit.photos && visit.photos.length > 0) ||
        visit.measurements || visit.structured_measurements ||
        visit.checklist || visit.site_conditions || visit.client_expectations;
        
      if (hasFieldData) {
        notes.push({
          id: visit.id,
          type: 'site_visit',
          siteVisitId: visit.id,
          serviceType: visit.service_type,
          jobAddress: visit.job_address || visit.address,
          date: visit.updated_at || visit.created_at,
          internalNotes: visit.field_notes?.internalNotes || visit.internal_notes,
          clientNotes: visit.field_notes?.clientNotes,
          projectNotes: visit.field_notes?.projectNotes || visit.project_notes,
          materialsIdeas: visit.field_notes?.materialsIdeas || visit.materials_scope_ideas,
          photos: visit.field_notes?.photos || visit.photos || [],
          sketches: visit.field_notes?.sketches || visit.sketches || [],
          measurements: visit.field_notes?.measurements || visit.measurements,
          structuredMeasurements: visit.field_notes?.structuredMeasurements || visit.structured_measurements,
          checklist: visit.field_notes?.checklist || visit.checklist,
          siteConditions: visit.field_notes?.siteConditions || visit.site_conditions,
          clientExpectations: visit.field_notes?.clientExpectations || visit.client_expectations,
          status: visit.status,
          isDraft: false
        });
      }
    });
    
    // Add estimate field notes
    estimates.forEach(estimate => {
      if (estimate.internal_notes || estimate.scope_of_work || 
          (estimate.measurements?.meeting_notes_summary) ||
          (estimate.photos && estimate.photos.length > 0) ||
          (estimate.sketches && estimate.sketches.length > 0)) {
        notes.push({
          id: estimate.id,
          type: 'estimate',
          estimateId: estimate.id,
          serviceType: estimate.service_type,
          jobAddress: estimate.job_address,
          date: estimate.created_at,
          internalNotes: estimate.internal_notes,
          scopeOfWork: estimate.scope_of_work,
          meetingNotes: estimate.measurements?.meeting_notes_summary,
          photos: estimate.photos || [],
          sketches: estimate.sketches || [],
          measurements: estimate.measurements,
          isDraft: false
        });
      }
    });
    
    // Sort by date descending (most recent first)
    notes.sort((a, b) => new Date(b.date) - new Date(a.date));
    return notes;
  };

  const fieldNotes = getAllFieldNotes();

  // Open photo lightbox
  const openPhotoLightbox = (photos, initialIndex = 0, title = 'Site Photos') => {
    setLightboxState({
      isOpen: true,
      photos,
      initialIndex,
      title
    });
  };

  const closeLightbox = () => {
    setLightboxState({ isOpen: false, photos: [], initialIndex: 0, title: '' });
  };

  // Calculate client stats
  const stats = {
    totalEstimates: estimates.length,
    totalInvoices: invoices.length,
    totalRevenue: invoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0),
    pendingAmount: invoices.reduce((sum, inv) => sum + (inv.amount_due - (inv.amount_paid || 0)), 0),
    approvedJobs: estimates.filter(e => e.status === 'approved').length,
    hasFieldNotes: fieldNotes.length > 0
  };

  // Check for scheduled or in-progress site visits
  const activeSiteVisit = siteVisits.find(v => 
    v.status === 'scheduled' || v.status === 'in_progress'
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col gap-4">
        {/* Top row: back button + name */}
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/clients')}
            className="p-2 flex-shrink-0"
            data-testid="back-to-clients"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground truncate">
              {client.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              Customer since {formatDistanceToNow(new Date(client.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        
        {/* Action buttons - stacked on mobile */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPdfPreview(true)}
            className="h-11 text-sm flex-1 sm:flex-none"
            data-testid="preview-profile-pdf"
          >
            <Eye className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">View Profile PDF</span>
            <span className="sm:hidden">PDF</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="h-11 px-4"
            data-testid="edit-client-btn"
          >
            {isEditing ? <X className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            onClick={handleArchive}
            className="h-11 px-4 text-amber-600 hover:bg-amber-50 border-amber-200"
            data-testid="archive-client-btn"
            title="Archive client"
          >
            <Archive className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-default p-4 text-center">
          <p className="text-3xl font-bold text-primary">{stats.totalEstimates}</p>
          <p className="text-sm text-muted-foreground">Estimates</p>
        </div>
        <div className="card-default p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{stats.approvedJobs}</p>
          <p className="text-sm text-muted-foreground">Jobs Won</p>
        </div>
        <div className="card-default p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">${stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-sm text-muted-foreground">Total Paid</p>
        </div>
        <div className="card-default p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">${stats.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-sm text-muted-foreground">Outstanding</p>
        </div>
      </div>
      
      {/* Active Site Visit Banner */}
      {activeSiteVisit && (
        <div 
          className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-all"
          onClick={() => navigate(`/site-visits/${activeSiteVisit.id}`)}
          data-testid="active-site-visit-banner"
        >
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              activeSiteVisit.status === 'in_progress' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              <CalendarCheck className={`w-6 h-6 ${
                activeSiteVisit.status === 'in_progress' ? 'text-green-600' : 'text-blue-600'
              }`} />
            </div>
            <div>
              <p className="font-semibold text-foreground">
                {activeSiteVisit.status === 'in_progress' ? 'Site Visit In Progress' : 'Site Visit Scheduled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {activeSiteVisit.service_type} • {activeSiteVisit.scheduled_date ? format(new Date(activeSiteVisit.scheduled_date), 'MMM d, yyyy') : 'Date TBD'}
                {activeSiteVisit.scheduled_time && ` at ${activeSiteVisit.scheduled_time}`}
              </p>
            </div>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="go-to-site-visit-btn">
            <CalendarCheck className="w-4 h-4 mr-2" />
            Go to Visit
          </Button>
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        // Scroll to top when switching tabs
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }} className="space-y-6">
        {/* Mobile-friendly horizontal scrolling tabs */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-auto min-w-full sm:w-full sm:max-w-2xl h-12 gap-1 sm:gap-0">
            <TabsTrigger value="overview" className="text-sm sm:text-base flex-shrink-0 px-3 sm:px-4" data-testid="tab-overview">
              <ClipboardList className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="field-notes" className="text-sm sm:text-base relative flex-shrink-0 px-3 sm:px-4" data-testid="tab-field-notes">
              <StickyNote className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Field Notes</span>
              {fieldNotes.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {fieldNotes.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="text-sm sm:text-base flex-shrink-0 px-3 sm:px-4" data-testid="tab-history">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-sm sm:text-base flex-shrink-0 px-3 sm:px-4" data-testid="tab-activity">
              <Clock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Activity</span>
            </TabsTrigger>
            <TabsTrigger value="conversations" className="text-sm sm:text-base flex-shrink-0 px-3 sm:px-4" data-testid="tab-conversations">
              <MessageCircle className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Conversations</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="text-sm sm:text-base flex-shrink-0 px-3 sm:px-4" data-testid="tab-notes">
              <MessageSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Notes</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {(() => {
            const allNiches = [
              resolveNiche(organization?.business_type || organization?.industry),
              ...(organization?.secondary_niches || []).map((n) => resolveNiche(n)),
            ];
            const showVehicles = allNiches.includes('detailing');
            return showVehicles ? (
              <div className="mb-6">
                <VehiclesSection
                  orgId={organization?.id}
                  client={client}
                  onUpdated={loadClientData}
                />
              </div>
            ) : null;
          })()}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Client Info */}
            <div className="lg:col-span-1">
              <div className="card-default">
                <h2 className="font-heading text-xl font-semibold mb-4">Contact Info</h2>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editData.name || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                        className="input-default mt-1"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editData.email || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                        className="input-default mt-1"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editData.phone || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-default mt-1"
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={editData.address || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                        className="input-default mt-1"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <textarea
                        value={editData.notes || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                        className="input-default mt-1 w-full min-h-[80px] py-3"
                      />
                    </div>
                    <Button
                      onClick={handleSave}
                      className="btn-primary w-full"
                      data-testid="save-client-btn"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {client.email && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <a href={`mailto:${client.email}`} className="font-medium text-primary hover:underline">
                            {client.email}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {client.phone && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <a href={`tel:${client.phone}`} className="font-medium text-primary hover:underline">
                            {client.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {client.address && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Address</p>
                          <p className="font-medium">{client.address}</p>
                        </div>
                      </div>
                    )}
                    
                    {client.notes && (
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-sm text-muted-foreground mb-1">Notes</p>
                        {Array.isArray(client.notes) ? (
                          <div className="space-y-2">
                            {client.notes.map((n, i) => (
                              <div key={n.id || i} className="bg-gray-50 rounded-lg p-2.5">
                                <p className="text-foreground text-sm whitespace-pre-wrap">
                                  {typeof n === 'string' ? n : (n.content || n.text || '')}
                                </p>
                                {typeof n === 'object' && (n.created_by || n.created_at) && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {n.created_by || 'Unknown'}
                                    {n.created_at && ` · ${new Date(n.created_at).toLocaleDateString()}`}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-foreground whitespace-pre-wrap">{String(client.notes)}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="card-default mt-6">
                <h2 className="font-heading text-xl font-semibold mb-4">Actions</h2>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/estimates/new', { state: { clientId: client.id } })}
                    className="w-full h-12 justify-start"
                    variant="outline"
                    data-testid="create-estimate-for-client"
                  >
                    <FileText className="w-5 h-5 mr-3 text-primary" />
                    Create Estimate
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Recent Activity */}
            <div className="lg:col-span-2">
              <div className="card-default">
                <h2 className="font-heading text-xl font-semibold mb-4">Recent Estimates</h2>
                {estimates.length > 0 ? (
                  <div className="space-y-3">
                    {estimates.slice(0, 5).map((estimate) => (
                      <button
                        key={estimate.id}
                        onClick={() => navigate(`/estimates/${estimate.id}`)}
                        className="w-full p-4 border border-gray-200 rounded-xl hover:border-primary transition-colors text-left"
                        data-testid={`estimate-${estimate.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{estimate.service_type}</p>
                            <p className="text-sm text-muted-foreground">
                              {estimate.job_address || 'No address'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">${estimate.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <span className={`status-${estimate.status}`}>
                              {estimate.status}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No estimates yet</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Field Notes Tab */}
        <TabsContent value="field-notes">
          <div className="card-default">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl font-semibold">Field Notes & Site Documentation</h2>
                <p className="text-muted-foreground">Notes, photos, and sketches from job site visits</p>
              </div>
            </div>
            
            {fieldNotes.length > 0 ? (
              <div className="space-y-6">
                {fieldNotes.map((note, index) => (
                  <div key={note.id || index} className={`border rounded-xl overflow-hidden ${
                    note.isDraft ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200'
                  }`}>
                    {/* Note Header */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      note.isDraft ? 'bg-amber-100' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center gap-3">
                        {note.isDraft && (
                          <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                            DRAFT
                          </span>
                        )}
                        <div>
                          <p className="font-semibold">{note.serviceType}</p>
                          <p className="text-sm text-muted-foreground">
                            {note.jobAddress || 'No address'} • {new Date(note.date).toLocaleDateString()}
                            {note.isDraft && ' • Auto-saved'}
                          </p>
                        </div>
                      </div>
                      {note.type === 'site_visit' || note.type === 'draft' ? (
                        <Button
                          variant={note.isDraft ? "default" : "outline"}
                          size="sm"
                          onClick={() => navigate(`/site-visits/${note.siteVisitId}`)}
                          className={note.isDraft ? "bg-amber-600 hover:bg-amber-700" : ""}
                        >
                          {note.isDraft ? 'Continue Editing' : 'View Site Visit'}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/estimates/${note.estimateId}`)}
                        >
                          View Estimate
                        </Button>
                      )}
                    </div>
                    
                    <div className="p-4 space-y-4">
                      {/* Internal Notes */}
                      {note.internalNotes && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <StickyNote className="w-4 h-4 text-primary" />
                            <span className="font-medium text-sm">Internal Notes</span>
                          </div>
                          <p className="text-sm bg-blue-50 p-3 rounded-lg whitespace-pre-wrap">
                            {note.internalNotes}
                          </p>
                        </div>
                      )}
                      
                      {/* Client Notes */}
                      {note.clientNotes && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-sm">Customer Notes</span>
                          </div>
                          <p className="text-sm bg-green-50 p-3 rounded-lg whitespace-pre-wrap">
                            {typeof note.clientNotes === 'string'
                              ? note.clientNotes
                              : Array.isArray(note.clientNotes)
                                ? note.clientNotes.map(n => typeof n === 'string' ? n : (n.content || n.text || '')).filter(Boolean).join('\n')
                                : JSON.stringify(note.clientNotes)}
                          </p>
                        </div>
                      )}
                      
                      {/* Scope of Work */}
                      {note.scopeOfWork && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <ClipboardList className="w-4 h-4 text-primary" />
                            <span className="font-medium text-sm">Scope of Work</span>
                          </div>
                          <p className="text-sm bg-blue-50 p-3 rounded-lg whitespace-pre-wrap">
                            {safeText(note.scopeOfWork)}
                          </p>
                        </div>
                      )}
                      
                      {/* Meeting Notes */}
                      {note.meetingNotes && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <StickyNote className="w-4 h-4 text-amber-600" />
                            <span className="font-medium text-sm">Meeting Notes</span>
                          </div>
                          <p className="text-sm bg-amber-50 p-3 rounded-lg whitespace-pre-wrap">
                            {safeText(note.meetingNotes)}
                          </p>
                        </div>
                      )}
                      
                      {/* Internal Notes */}
                      {note.internalNotes && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-sm">Internal Notes</span>
                          </div>
                          <p className="text-sm bg-gray-100 p-3 rounded-lg whitespace-pre-wrap">
                            {safeText(note.internalNotes)}
                          </p>
                        </div>
                      )}
                      
                      {/* Photos */}
                      {note.photos && note.photos.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Camera className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-sm">Site Photos ({note.photos.length})</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPhotoLightbox(note.photos, 0, `${note.serviceType} - Site Photos`)}
                              className="h-8 text-xs text-primary hover:bg-primary/10"
                              data-testid="view-all-photos-btn"
                            >
                              <Expand className="w-3 h-3 mr-1" />
                              View All
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {note.photos.map((photo, i) => {
                              // Handle different photo formats: string URL, {url}, {dataUrl}
                              const photoUrl = typeof photo === 'string' 
                                ? photo 
                                : (photo.dataUrl || photo.url || photo.src || '');
                              
                              return (
                                <button
                                  key={i}
                                  onClick={() => openPhotoLightbox(note.photos, i, `${note.serviceType} - Site Photos`)}
                                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                                  data-testid={`photo-thumbnail-${i}`}
                                >
                                  {photoUrl ? (
                                    <img 
                                      src={photoUrl}
                                      alt={photo.caption || `Site photo ${i + 1}`}
                                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.classList.add('bg-gray-200');
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      <Camera className="w-8 h-8" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <Expand className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Sketches */}
                      {note.sketches && note.sketches.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <PenTool className="w-4 h-4 text-purple-600" />
                              <span className="font-medium text-sm">Sketches ({note.sketches.length})</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPhotoLightbox(note.sketches, 0, `${note.serviceType} - Sketches`)}
                              className="h-8 text-xs text-primary hover:bg-primary/10"
                              data-testid="view-all-sketches-btn"
                            >
                              <Expand className="w-3 h-3 mr-1" />
                              View All
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {note.sketches.map((sketch, i) => {
                              // Handle different sketch formats
                              const sketchUrl = typeof sketch === 'string' 
                                ? sketch 
                                : (sketch.dataUrl || sketch.url || sketch.src || '');
                              
                              return (
                                <button
                                  key={i}
                                  onClick={() => openPhotoLightbox(note.sketches, i, `${note.serviceType} - Sketches`)}
                                  className="aspect-square rounded-lg overflow-hidden bg-gray-50 border relative group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                                  data-testid={`sketch-thumbnail-${i}`}
                                >
                                  {sketchUrl ? (
                                    <img 
                                      src={sketchUrl}
                                      alt={sketch.caption || `Sketch ${i + 1}`}
                                      className="w-full h-full object-contain transition-transform group-hover:scale-105"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.classList.add('bg-gray-200');
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      <PenTool className="w-8 h-8" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                    <Expand className="w-6 h-6 text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Project Notes / Materials & Ideas */}
                      {(note.projectNotes || note.materialsIdeas) && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <ClipboardList className="w-4 h-4 text-indigo-600" />
                            <span className="font-medium text-sm">Project Notes & Ideas</span>
                          </div>
                          {note.projectNotes && (
                            <p className="text-sm bg-indigo-50 p-3 rounded-lg whitespace-pre-wrap mb-2">
                              {safeText(note.projectNotes)}
                            </p>
                          )}
                          {note.materialsIdeas && (
                            <p className="text-sm bg-indigo-50 p-3 rounded-lg whitespace-pre-wrap">
                              <strong>Materials & Scope Ideas:</strong> {safeText(note.materialsIdeas)}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Measurements */}
                      {(note.measurements || note.structuredMeasurements) && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Ruler className="w-4 h-4 text-orange-600" />
                            <span className="font-medium text-sm">Measurements</span>
                          </div>
                          <div className="bg-orange-50 p-3 rounded-lg">
                            {note.structuredMeasurements && Array.isArray(note.structuredMeasurements) && note.structuredMeasurements.length > 0 ? (
                              <div className="space-y-2">
                                {note.structuredMeasurements.map((m, i) => (
                                  <div key={i} className="text-sm">
                                    <div className="flex justify-between">
                                      <span className="font-medium">{m.customLabel || m.label || m.name || `Item ${i+1}`}</span>
                                      <span>
                                        {m.length && m.width ? (
                                          <>{m.length} × {m.width} = {m.value} sq {m.unit || 'ft'}</>
                                        ) : (
                                          <>{m.value} {m.unit || ''}</>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : note.measurements && typeof note.measurements === 'object' ? (
                              <div className="space-y-2">
                                {Object.entries(note.measurements).filter(([k,v]) => v && k !== 'meeting_notes_summary').map(([key, value]) => (
                                  <div key={key} className="flex justify-between text-sm">
                                    <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                    <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{String(note.measurements)}</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Checklist */}
                      {note.checklist && (Array.isArray(note.checklist) ? note.checklist.length > 0 : Object.keys(note.checklist).length > 0) && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-teal-600" />
                            <span className="font-medium text-sm">Site Visit Checklist</span>
                          </div>
                          <div className="bg-teal-50 p-3 rounded-lg">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {Array.isArray(note.checklist) ? (
                                // Handle array format: [{id, text, checked}, ...]
                                note.checklist.map((item, idx) => (
                                  <div key={item.id || idx} className="flex items-center gap-2">
                                    {item.checked ? (
                                      <CheckCircle className="w-4 h-4 text-teal-600" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-gray-400" />
                                    )}
                                    <span className={item.checked ? '' : 'text-muted-foreground'}>
                                      {item.text || item.label || `Item ${idx + 1}`}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                // Handle object format: {key: value, ...}
                                Object.entries(note.checklist).map(([key, value]) => (
                                  <div key={key} className="flex items-center gap-2">
                                    {value ? (
                                      <CheckCircle className="w-4 h-4 text-teal-600" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-gray-400" />
                                    )}
                                    <span className={value ? '' : 'text-muted-foreground'}>
                                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Site Conditions - only show checked conditions */}
                      {note.siteConditions && (Array.isArray(note.siteConditions) ? note.siteConditions.some(c => c.checked) : Object.values(note.siteConditions).some(v => v)) && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-600" />
                            <span className="font-medium text-sm">Site Conditions</span>
                          </div>
                          <div className="bg-yellow-50 p-3 rounded-lg text-sm">
                            {Array.isArray(note.siteConditions) ? (
                              <ul className="list-disc pl-4 space-y-1">
                                {note.siteConditions.filter(c => c.checked).map((condition, i) => (
                                  <li key={condition.id || i}>
                                    <span className="font-medium">
                                      {condition.name || condition.label || condition.id}
                                    </span>
                                    {condition.notes && (
                                      <span className="text-muted-foreground ml-1">- {condition.notes}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : typeof note.siteConditions === 'object' ? (
                              <div className="space-y-1">
                                {Object.entries(note.siteConditions).filter(([k,v]) => v).map(([key, value]) => (
                                  <p key={key}>
                                    <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                    {typeof value === 'string' && value !== 'true' && value !== key && (
                                      <span className="text-muted-foreground ml-1">- {value}</span>
                                    )}
                                  </p>
                                ))}
                              </div>
                            ) : (
                              <p>{String(note.siteConditions)}</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Client Expectations */}
                      {note.clientExpectations && (
                        (() => {
                          const ce = note.clientExpectations;
                          const hasContent = typeof ce === 'object' 
                            ? (ce.wantsDone || ce.what_they_want || ce.timeline || ce.budgetRange || ce.budget_range || ce.budget || ce.specialRequests || ce.special_requests || ce.priorities || ce.notes)
                            : !!ce;
                          if (!hasContent) return null;
                          
                          // Map budget range codes to display labels
                          const budgetLabels = {
                            'under_1k': 'Under $1,000',
                            '1k_5k': '$1,000 - $5,000',
                            '5k_10k': '$5,000 - $10,000',
                            '10k_25k': '$10,000 - $25,000',
                            '25k_50k': '$25,000 - $50,000',
                            '50k_100k': '$50,000 - $100,000',
                            'over_100k': 'Over $100,000',
                            'not_discussed': 'Not discussed'
                          };
                          const rawBudget = ce.budgetRange || ce.budget_range || ce.budget || '';
                          const budgetDisplay = budgetLabels[rawBudget] || rawBudget;

                          return (
                            <div data-testid="client-expectations-section">
                              <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-pink-600" />
                                <span className="font-medium text-sm">Client Expectations</span>
                              </div>
                              <div className="bg-pink-50 p-3 rounded-lg text-sm">
                                {typeof ce === 'object' ? (
                                  <div className="space-y-2">
                                    {(ce.wantsDone || ce.what_they_want) && (
                                      <p><strong>What Client Wants Done:</strong> {ce.wantsDone || ce.what_they_want}</p>
                                    )}
                                    {ce.timeline && (
                                      <p><strong>Timeline:</strong> {ce.timeline}</p>
                                    )}
                                    {budgetDisplay && rawBudget && rawBudget !== 'not_discussed' && (
                                      <p><strong>Budget:</strong> {budgetDisplay}</p>
                                    )}
                                    {(ce.specialRequests || ce.special_requests) && (
                                      <p><strong>Special Requests:</strong> {ce.specialRequests || ce.special_requests}</p>
                                    )}
                                    {ce.priorities && (
                                      <p><strong>Priorities:</strong> {Array.isArray(ce.priorities) ? ce.priorities.join(', ') : ce.priorities}</p>
                                    )}
                                    {ce.notes && (
                                      <p><strong>Notes:</strong> {ce.notes}</p>
                                    )}
                                  </div>
                                ) : (
                                  <p>{ce}</p>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <StickyNote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No field notes yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Field notes are captured during estimate creation using the Meeting Notes feature
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Estimates */}
            <div className="card-default">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold">All Estimates</h2>
                <span className="text-muted-foreground">{estimates.length} total</span>
              </div>
              
              {estimates.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {estimates.map((estimate) => (
                    <button
                      key={estimate.id}
                      onClick={() => navigate(`/estimates/${estimate.id}`)}
                      className="w-full p-4 border border-gray-200 rounded-xl hover:border-primary transition-colors text-left"
                      data-testid={`estimate-${estimate.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{estimate.service_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(estimate.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">${estimate.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <span className={`status-${estimate.status}`}>
                            {estimate.status}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No estimates yet</p>
              )}
            </div>
            
            {/* Invoices */}
            <div className="card-default">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold">All Invoices</h2>
                <span className="text-muted-foreground">{invoices.length} total</span>
              </div>
              
              {invoices.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {invoices.map((invoice) => (
                    <button
                      key={invoice.id}
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      className="w-full p-4 border border-gray-200 rounded-xl hover:border-primary transition-colors text-left"
                      data-testid={`invoice-${invoice.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Invoice #{invoice.id.slice(0, 8).toUpperCase()}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">${invoice.amount_due?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <span className={`status-${invoice.status}`}>
                            {invoice.status}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No invoices yet</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Activity Log Tab - Who did what when */}
        <TabsContent value="activity">
          <div className="card-default">
            <CustomerActivityHistory clientId={clientId} />
          </div>
        </TabsContent>

        {/* Conversations Tab */}
        <TabsContent value="conversations">
          <ConversationsTab
            clientId={clientId}
            client={client}
            estimates={estimates}
            organization={organization}
            user={user}
          />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Notes Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">Notes</h2>
              
              {/* Add Note Form */}
              <div className="mb-4">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this client..."
                  rows={3}
                  className="mb-2"
                />
                <Button 
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className="btn-primary"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </div>
              
              {/* Notes List */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {clientNotes.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No notes yet</p>
                ) : (
                  clientNotes.map((note) => (
                    <div key={note.id} className="bg-gray-50 rounded-lg p-3 relative group">
                      <p className="text-sm pr-8">{note.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {note.created_by} • {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                      </p>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">Activity Timeline</h2>
              
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {activityLog.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No activity yet</p>
                ) : (
                  activityLog.map((activity, idx) => (
                    <div key={activity.id + idx} className="flex gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'estimate' ? 'bg-blue-100 text-blue-600' :
                        activity.type === 'invoice' ? 'bg-amber-100 text-amber-600' :
                        activity.type === 'job' ? 'bg-green-100 text-green-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {activity.type === 'estimate' && <FileText className="w-5 h-5" />}
                        {activity.type === 'invoice' && <Receipt className="w-5 h-5" />}
                        {activity.type === 'job' && <Briefcase className="w-5 h-5" />}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{activity.action}</p>
                        <button
                          onClick={() => navigate(activity.link)}
                          className="text-sm text-blue-600 hover:underline truncate block"
                        >
                          {activity.title}
                        </button>
                        {activity.amount > 0 && (
                          <p className="text-sm text-muted-foreground">
                            ${activity.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {activity.date ? formatDistanceToNow(new Date(activity.date), { addSuffix: true }) : 'Unknown date'}
                        </p>
                      </div>
                      
                      {/* Status Badge */}
                      <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                        activity.status === 'approved' || activity.status === 'paid' || activity.status === 'completed' 
                          ? 'bg-green-100 text-green-700' :
                        activity.status === 'declined' 
                          ? 'bg-red-100 text-red-700' :
                        activity.status === 'sent' || activity.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {activity.status}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        pdfUrl={organization?.id && clientId ? `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/clients/${clientId}/profile-pdf` : null}
        title="Client Profile"
        documentNumber={client?.name}
      />
      
      {/* Photo Lightbox */}
      <PhotoLightbox
        isOpen={lightboxState.isOpen}
        onClose={closeLightbox}
        photos={lightboxState.photos}
        initialIndex={lightboxState.initialIndex}
        title={lightboxState.title}
      />
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default ClientDetailPage;
